import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useSearchParams, useLocation, useNavigationType } from 'react-router-dom';
import { get } from '@/features/shared/lib/api';
import { arr, parsePayload, buildListParams } from '@/features/shared/lib/utils';
import Paginator from '@/features/shared/ui/Paginator';
import PublicationCard from '@/features/publication/components/PublicationCard';
import JournalCard from "@/features/journal/components/JournalCard";
import { FacetBlock } from "@/features/shared/components/Facet";
import PublicationGraphs from '@/features/publication/components/PublicationGraphs';
import PublicationSortSelect from '@/features/publication/components/PublicationSortSelect';
import PageBreadcrumb from '@/features/shared/components/PageBreadcrumb';
import PublicationsFilterSidebar from '@/features/publication/components/PublicationsFilterSidebar';
import { FacetPopup } from "@/features/shared/components/FacetPopup";
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';
import { useMetricsWidgets } from '@/features/shared/lib/metrics-widgets';

export default function Publications() {
  // Back-navigation cache helpers
  const location = useLocation();
  const navType = useNavigationType();
  const hydratedRef = useRef(false);
  const cacheKey = useMemo(() => {
    try {
      const { pathname, search } = window.location;
      return `PUBLICATIONS_CACHE::${pathname}${search}`;
    } catch (e) {
      return 'PUBLICATIONS_CACHE';
    }
  }, [location]);
  const loadCache = () => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const saveCache = (payload) => {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch (e) {
    }
  };

  // Cache TTL and validator (avoid using empty or stale caches)
  const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
  const isValidCache = (c) => {
    if (!c) return false;
    if (!Array.isArray(c.rows) || c.rows.length === 0) return false;
    if (typeof c.ts !== 'number') return true; // backward compatibility
    return (Date.now() - c.ts) <= CACHE_TTL_MS;
  };

  // Detect real browser back/forward navigation (not initial load)
  const isBackForwardNav = () => {
    try {
      const navs = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (navs && navs.length) {
        const type = navs[0]?.type;
        if (type === 'back_forward') return true;
        if (type === 'reload') return false;
        return false;
      }
    } catch (e) {
    }
    // Fallback: use history.state.idx only when referrer is same-origin
    try {
      const idx = window.history && window.history.state && window.history.state.idx;
      const sameOriginReferrer = document.referrer && document.referrer.startsWith(window.location.origin);
      if (typeof idx === 'number' && idx > 0 && sameOriginReferrer) return true;
    } catch (e) { }
    return false;
  };

  // Pre-hydrate initial state from cache when coming back via POP
  const initialCacheRaw = (navType === 'POP' && isBackForwardNav()) ? loadCache() : null;
  const initialCache = isValidCache(initialCacheRaw) ? initialCacheRaw : null;

  const [rows, setRows] = useState(Array.isArray(initialCache?.rows) ? initialCache.rows : []);
  const [pageInfo, setPageInfo] = useState(initialCache?.pageInfo || { fromPage: 1, totalPage: 1, resultsPerPage: 20, totalResults: 0 });
  const [facet, setFacet] = useState(initialCache?.facet || {});
  // 초기 로딩 상태: 캐시가 없으면 true, 있으면 false
  const [loading, setLoading] = useState(!initialCache);
  const [err, setErr] = useState(null);
  // 첫 로딩인지 추적 (첫 API 호출일 때만 로딩 컴포넌트 표시)
  const isFirstLoadRef = useRef(!initialCache);

  // filters: { [facetKey]: string[] } - applied filters
  const [filters, setFilters] = useState({});
  // pendingFilters: staged selections applied on Refine
  const [pendingFilters, setPendingFilters] = useState({});
  // conjunctions: { [facetKey]: 'and' | null } - conjunction state for facets
  const [conjunctions, setConjunctions] = useState({});
  // sort: { order_by: string, order: 'asc'|'desc' }
  const [sort, setSort] = useState({ order_by: 'publish_date', order: 'desc' });
  const [popup, setPopup] = useState({ open: false, key: '', title: '' });
  // free-text query for search within results (API param: sub_ks)
  const [query, setQuery] = useState('');
  // keyword from URL (API param: keyword)
  const [keyword, setKeyword] = useState('');
  // graph toggle state
  const [graphExpanded, setGraphExpanded] = useState(true);
  
  // URL에 검색 또는 패싯 관련 파라미터가 있으면 그래프 접기
  const checkIfSearchMode = () => {
    try {
      const sp = new URLSearchParams(window.location.search);
      // scholar_id 파라미터 체크
      if (sp.get('scholar_id')) return true;
      // 검색어(sub_ks) 체크
      if (sp.get('sub_ks')) return true;
      // keyword 파라미터 체크
      if (sp.get('keyword')) return true;
      // 필터 파라미터 체크 ([]로 끝나는 파라미터)
      let hasFilter = false;
      sp.forEach((_, k) => {
        if (k.endsWith('[]')) {
          hasFilter = true;
        }
      });
      if (hasFilter) return true;
      // hot 파라미터 체크
      if (sp.get('hot')) return true;
      // conjunction 파라미터 체크
      let hasConjunction = false;
      sp.forEach((_, k) => {
        if (k.endsWith('_conjunction')) {
          hasConjunction = true;
        }
      });
      if (hasConjunction) return true;
      return false;
    } catch (e) {
      return false;
    }
  };

  // If we have an initial cache, seed states up-front to avoid first-paint flicker
  const [rowsState, setRowsState] = useState(Array.isArray(initialCache?.rows) ? initialCache.rows : rows);
  const [pageInfoState, setPageInfoState] = useState(initialCache?.pageInfo || pageInfo);
  const [facetState, setFacetState] = useState(initialCache?.facet || facet);
  const [filtersState, setFiltersState] = useState(initialCache?.filters || filters);
  const [pendingFiltersState, setPendingFiltersState] = useState(initialCache?.pendingFilters || pendingFilters);
  const [sortState, setSortState] = useState(initialCache?.sort || sort);

  // Bridge state vars back to canonical ones if used
  useEffect(() => {
    if (initialCache) {
      hydratedRef.current = true;
      setRows(rowsState);
      setPageInfo(pageInfoState);
      setFacet(facetState);
      setFilters(filtersState);
      setPendingFilters(pendingFiltersState);
      setSort(sortState);
      setTimeout(() => {
        try { window.scrollTo({ top: initialCache.scrollY || 0, left: 0, behavior: 'auto' }); } catch (e) { }
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildExtraParams = () => {
    const extra = {};
    // facet arrays as key[]=value
    Object.entries(filters || {}).forEach(([k, arrVals]) => {
      if (k === 'hot') {
        // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
        if (Array.isArray(arrVals) && arrVals.length) {
          extra.hot = arrVals[0];
        }
      } else if (Array.isArray(arrVals) && arrVals.length) {
        extra[`${k}[]`] = arrVals;
      }
    });
    // conjunction parameters
    Object.entries(conjunctions || {}).forEach(([k, conj]) => {
      if (conj === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (sort?.order_by) extra.order_by = sort.order_by;
    if (sort?.order) extra.order = sort.order;
    // free text query (API expects sub_ks)
    if (String(query || '').trim()) extra.sub_ks = String(query).trim();
    // keyword from URL
    if (String(keyword || '').trim()) extra.keyword = String(keyword).trim();
    return extra;
  };

  const cleanFilters = (f = {}) => {
    const out = {};
    Object.entries(f).forEach(([k, vals]) => {
      if (Array.isArray(vals) && vals.length) out[k] = vals;
    });
    return out;
  };

  const fetchList = async (page = 1, opt = {}) => {
    // 첫 로딩일 때만 loading 상태를 true로 설정
    if (isFirstLoadRef.current) {
      setLoading(true);
    }
    setErr(null);
    try {
      const extra = opt.extra ? opt.extra : buildExtraParams();

      // URL 쿼리 파라미터에서 scholar_id, keyword 읽기
      const sp = new URLSearchParams(window.location.search);
      const scholarId = sp.get('scholar_id');
      if (scholarId) {
        extra.scholar_id = scholarId;
      }
      const kw = sp.get('keyword');
      if (kw) {
        extra.keyword = kw;
      }

      const params = buildListParams({ page, perPage: pageInfo.resultsPerPage || 20, extra });
      const data = await get('/rm/v1/publications', { params });
      const { status, records, pageInfo: pi, facet: fc } = parsePayload(data);
      if (!status) throw new Error('API status=false');
      const nextRows = records;
      const nextPageInfo = {
        fromPage: pi.current_page ?? pi.fromPage ?? page,
        totalPage: pi.totalPage ?? pi.total_page ?? 1,
        resultsPerPage: pi.resultsPerPage ?? pi.per_page ?? params.record_per_page ?? 20,
        totalResults: pi.totalResults ?? pi.total_results ?? 0,
      };
      const nextFacet = fc || {};
      setRows(nextRows);
      setPageInfo(nextPageInfo);
      setFacet(nextFacet);
      // save cache after successful fetch
      saveCache({
        rows: nextRows,
        pageInfo: nextPageInfo,
        facet: nextFacet,
        filters,
        pendingFilters,
        conjunctions,
        sort,
        scrollY: window.scrollY || 0,
        ts: Date.now(),
      });
    } catch (e) {
      console.error('[PUBLICATIONS][fetchList]', e);
      setErr(e?.response?.data?.message || e.message);
    } finally {
      if (isFirstLoadRef.current) {
        setLoading(false);
        isFirstLoadRef.current = false; // 첫 로딩 완료 후 플래그 해제
      }
    }
  };

  // URL 동기화 유틸
  const syncUrl = ({ page, filters: f = filters, sort: s = sort, conjunctions: c = {} } = {}) => {
    const sp = new URLSearchParams(window.location.search);
    // page
    if (page) sp.set('current_page', String(page)); else sp.delete('current_page');
    // sort
    if (s?.order_by) sp.set('order_by', s.order_by); else sp.delete('order_by');
    if (s?.order) sp.set('order', s.order); else sp.delete('order');
    // free text query (API & URL param: sub_ks)
    const qv = String(query || '').trim();
    if (qv) sp.set('sub_ks', qv); else sp.delete('sub_ks');
    // keyword
    const kwv = String(keyword || '').trim();
    if (kwv) sp.set('keyword', kwv); else sp.delete('keyword');
    // clear existing facet [] keys and conjunction keys
    const toDelete = [];
    sp.forEach((_, k) => {
      if (k.endsWith('[]')) toDelete.push(k);
      if (k.endsWith('_conjunction')) toDelete.push(k);
      if (k === 'hot') toDelete.push(k); // hot도 삭제
    });
    toDelete.forEach(k => sp.delete(k));
    // add current filters as key[]=value
    Object.entries(f || {}).forEach(([k, vals]) => {
      if (k === 'hot') {
        // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
        if (Array.isArray(vals) && vals.length) {
          sp.set('hot', vals[0]);
        }
      } else if (Array.isArray(vals)) {
        vals.forEach(v => sp.append(`${k}[]`, v));
      }
    });
    // add conjunction parameters
    Object.entries(c || {}).forEach(([k, conj]) => {
      if (conj === 'and') {
        sp.set(`${k}_conjunction`, 'and');
      }
    });
    const newUrl = `${window.location.pathname}?${sp.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  useLayoutEffect(() => {
    // POP로 들어왔고 아직 hydrate 안 되었을 때 캐시 상태를 먼저 주입하고 스크롤을 즉시 복원
    if (navType !== 'POP' || hydratedRef.current) return;
    const cached = loadCache();
    if (isValidCache(cached)) {
      hydratedRef.current = true;
      setRows(cached.rows || []);
      setPageInfo(cached.pageInfo || pageInfo);
      setFacet(cached.facet || {});
      if (cached.sort) setSort(cached.sort);
      if (cached.filters) {
        setFilters(cached.filters);
        setPendingFilters(cached.pendingFilters || cached.filters);
      }
      if (cached.conjunctions) {
        setConjunctions(cached.conjunctions);
      }
      try {
        window.scrollTo({ top: cached.scrollY || 0, left: 0, behavior: 'auto' });
      } catch (e) { }
    }
  }, [navType]);

  useEffect(() => {
    // On true back/forward with valid cache, skip initial fetch (already hydrated)
    if (navType === 'POP' && isBackForwardNav()) {
      const cached = loadCache();
      if (isValidCache(cached)) {
        hydratedRef.current = true;
        if (!rows || rows.length === 0) {
          setRows(cached.rows || []);
          setPageInfo(cached.pageInfo || pageInfo);
          setFacet(cached.facet || {});
          if (cached.sort) setSort(cached.sort);
          if (cached.filters) setFilters(cached.filters);
          if (cached.pendingFilters) setPendingFilters(cached.pendingFilters);
          if (cached.conjunctions) setConjunctions(cached.conjunctions);
        }
        setTimeout(() => {
          try { window.scrollTo({ top: cached.scrollY || 0, left: 0, behavior: 'auto' }); } catch (e) { }
        }, 0);
        return; // skip initial fetch
      }
    }

    const sp = new URLSearchParams(window.location.search);
    const initPage = Number(sp.get('current_page')) || 1;
    const hasKeyword = !!(sp.get('ks') || sp.get('sub_ks') || sp.get('keyword'));
    const initSort = { order_by: sp.get('order_by') || (hasKeyword ? 'score' : 'publish_date'), order: sp.get('order') || 'desc' };
    const initFilters = {};
    const initConjunctions = {};
    sp.forEach((v, k) => {
      if (k.endsWith('[]')) {
        const base = k.slice(0, -2);
        const all = sp.getAll(k);
        initFilters[base] = Array.from(new Set(all));
      } else if (k === 'hot') {
        // hot 파라미터는 배열로 저장
        initFilters.hot = [v];
      } else if (k.endsWith('_conjunction')) {
        const base = k.replace('_conjunction', '');
        if (v === 'and') {
          initConjunctions[base] = 'and';
        }
      }
    });
    const initQuery = sp.get('sub_ks') || '';
    const initKeyword = sp.get('keyword') || '';
    setQuery(initQuery);
    setKeyword(initKeyword);
    setSort(initSort);
    setFilters(initFilters);
    setPendingFilters(initFilters);
    setConjunctions(initConjunctions);
    // 검색·패싯 관련 URL이 있으면 그래프 접기
    if (checkIfSearchMode()) {
      setGraphExpanded(false);
    }
    const extra = {};
    Object.entries(initFilters).forEach(([k, vals]) => {
      if (k === 'hot') {
        // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
        if (Array.isArray(vals) && vals.length) {
          extra.hot = vals[0];
        }
      } else if (Array.isArray(vals) && vals.length) {
        extra[`${k}[]`] = vals;
      }
    });
    Object.entries(initConjunctions).forEach(([k, v]) => {
      if (v === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (initSort.order_by) extra.order_by = initSort.order_by;
    if (initSort.order) extra.order = initSort.order;
    if (String(initQuery || '').trim()) extra.sub_ks = String(initQuery).trim();
    if (String(initKeyword || '').trim()) extra.keyword = String(initKeyword).trim();
    fetchList(initPage, { extra });
  }, [navType]);

  // URL에 검색·패싯 관련 파라미터가 있으면 그래프 접기 (URL 변경 시마다 체크)
  useEffect(() => {
    if (checkIfSearchMode()) {
      setGraphExpanded(false);
    } else {
      setGraphExpanded(true);
    }
  }, [location.search]);

  // Altmetric/PlumX 위젯 스크립트 로드/초기화 (공통 훅)
  // 페이지별로 delaysMs 값만 바꿔서 "느리게 호출" 등 대응 가능
  useMetricsWidgets({
    enabled: !loading && Array.isArray(rows) && rows.length > 0,
    delaysMs: { altmetric: 1000, plumx: 1000 },
    cleanupBeforeLoad: true,
    plumxRemoveRenderedDom: true,
    plumxRenderedSelector: '.plx-wrapping-print-link',
    plumxWireUpDelayMs: 150,
  });

  // keep scroll position in cache; do not create empty caches
  useEffect(() => {
    const onScroll = () => {
      const cached = loadCache();
      const now = Date.now();
      if (Array.isArray(rows) && rows.length > 0) {
        const next = {
          rows,
          pageInfo,
          facet,
          filters,
          pendingFilters,
          conjunctions,
          sort,
          scrollY: window.scrollY || 0,
          ts: now,
        };
        saveCache(next);
      } else if (isValidCache(cached)) {
        cached.scrollY = window.scrollY || 0;
        cached.ts = now;
        saveCache(cached);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [rows, pageInfo, facet, filters, pendingFilters, sort]);


  const onPage = (p) => {
    if (!p || p === pageInfo.fromPage) return;
    // 페이징 시 스크립트는 유지 (한 번만 로드되어야 함)
    syncUrl({ page: p });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) {
      // no-op
    }
    fetchList(p);
  };

  // trigger search using current query and filters, resets to page 1
  const doSearch = () => {
    setGraphExpanded(false);
    const scoreSort = { order_by: 'score', order: 'desc' };
    setSort(scoreSort);
    syncUrl({ page: 1, conjunctions, sort: scoreSort });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) {
    }
    const extra = buildExtraParams();
    extra.order_by = 'score';
    extra.order = 'desc';
    fetchList(1);
  };

  // hot 필터에 따른 제목 매핑
  const hotTitleMap = {
    'hcp': 'Highly Cited Papers',
    'hp': 'Hot Papers',
  };

  // 현재 선택된 hot 필터 (Notable Publications 섹션)
  const currentHot = Array.isArray(filters.hot) && filters.hot.length > 0 ? filters.hot[0] : null;
  const hotTitle = currentHot ? hotTitleMap[currentHot] || 'Publications' : 'Publications';

  return (
    <div className="publicationlistPage listPage flexPage mt-[127px] lg:mt-[120px] 2xl:px-[100px] px-6">
      <div className="lg:w-[310px] lg:mt-[45px]">
        <div className="highlight-resource-box fixed top-[100%] right-0 z-[101] w-full h-full transition-top duration-300 mb-8 p-8 bg-white rounded-xl lg:static">
          <h2 className="flex items-center gap-[6px] mb-5">
            <span className="text-[18px] font-bold">Notable Publications</span>
          </h2>

          <button className="highlight-resource-box-close absolute top-8 right-8 w-[25px] h-[25px] cursor-pointer lg:hidden">
            <img
              src={`${rmVars.themeUrl}images/clear-icon.png`}
              className="w-[18px]"
              alt="close"
            />
          </button>

          <div className="flex flex-col gap-2 lg:gap-1">
            <button
              onClick={(e) => {
                e.preventDefault();
                setGraphExpanded(false);
                const isActive = Array.isArray(filters.hot) && filters.hot.includes('hcp');
                const newFilters = { ...filters };
                if (isActive) {
                  // 이미 선택되어 있으면 해제
                  const updatedHot = (filters.hot || []).filter(v => v !== 'hcp');
                  if (updatedHot.length > 0) {
                    newFilters.hot = updatedHot;
                  } else {
                    delete newFilters.hot;
                  }
                } else {
                  // 선택되어 있지 않으면 선택
                  newFilters.hot = ['hcp'];
                }
                setFilters(newFilters);
                const extra = {};
                Object.entries(newFilters).forEach(([k, vals]) => {
                  if (k === 'hot') {
                    // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
                    if (Array.isArray(vals) && vals.length) {
                      extra.hot = vals[0];
                    }
                  } else if (Array.isArray(vals) && vals.length) {
                    extra[`${k}[]`] = vals;
                  }
                });
                Object.entries(conjunctions).forEach(([k, conj]) => {
                  if (conj === 'and') {
                    extra[`${k}_conjunction`] = 'and';
                  }
                });
                if (sort?.order_by) extra.order_by = sort.order_by;
                if (sort?.order) extra.order = sort.order;
                if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                syncUrl({ page: 1, filters: newFilters, sort, conjunctions });
                try {
                  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }
                } catch (err) { }
                fetchList(1, { extra });
              }}
              className={`px-[14px] py-[10px] rounded-md text-left cursor-pointer hover:bg-[#D1D5DB] transition-colors ${Array.isArray(filters.hot) && filters.hot.includes('hcp') ? 'active bg-[#D1D5DB]' : 'bg-[#F3F5F9]'}`}
            >
              <span className="text-[#376BDB] text-[14px] font-semibold">
                Highly Cited Papers
              </span>
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                setGraphExpanded(false);
                const isActive = Array.isArray(filters.hot) && filters.hot.includes('hp');
                const newFilters = { ...filters };
                if (isActive) {
                  // 이미 선택되어 있으면 해제
                  const updatedHot = (filters.hot || []).filter(v => v !== 'hp');
                  if (updatedHot.length > 0) {
                    newFilters.hot = updatedHot;
                  } else {
                    delete newFilters.hot;
                  }
                } else {
                  // 선택되어 있지 않으면 선택
                  newFilters.hot = ['hp'];
                }
                setFilters(newFilters);
                const extra = {};
                Object.entries(newFilters).forEach(([k, vals]) => {
                  if (k === 'hot') {
                    // hot 필터는 hot[] 대신 hot으로 첫 번째 값만 전달
                    if (Array.isArray(vals) && vals.length) {
                      extra.hot = vals[0];
                    }
                  } else if (Array.isArray(vals) && vals.length) {
                    extra[`${k}[]`] = vals;
                  }
                });
                Object.entries(conjunctions).forEach(([k, conj]) => {
                  if (conj === 'and') {
                    extra[`${k}_conjunction`] = 'and';
                  }
                });
                if (sort?.order_by) extra.order_by = sort.order_by;
                if (sort?.order) extra.order = sort.order;
                if (String(query || '').trim()) extra.sub_ks = String(query).trim();
                syncUrl({ page: 1, filters: newFilters, sort, conjunctions });
                try {
                  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }
                } catch (err) { }
                fetchList(1, { extra });
              }}
              className={`px-[14px] py-[10px] rounded-md text-left cursor-pointer hover:bg-[#D1D5DB] transition-colors ${Array.isArray(filters.hot) && filters.hot.includes('hp') ? 'active bg-[#D1D5DB]' : 'bg-[#F3F5F9]'}`}
            >
              <span className="text-[#376BDB] text-[14px] font-semibold">Hot Papers</span>
            </button>
          </div>
        </div>

        <PublicationsFilterSidebar
          facet={facet}
          filters={filters}
          setFilters={setFilters}
          pendingFilters={pendingFilters}
          setPendingFilters={setPendingFilters}
          conjunctions={conjunctions}
          setConjunctions={setConjunctions}
          sort={sort}
          query={query}
          setQuery={setQuery}
          doSearch={doSearch}
          syncUrl={syncUrl}
          fetchList={fetchList}
          cleanFilters={cleanFilters}
          setPopup={setPopup}
          setGraphExpanded={setGraphExpanded}
        />
      </div>

      <div className="publication-list-cont w-full px-6 pt-[30px] lg:w-[calc(100%-360px)] lg:px-[0] lg:pt-[23px] lg:mt-[50px]">
        <PageBreadcrumb current="Publications" />

        <div className="tit-area w-full flex justify-between gap-3">
          <h2 className="text-[26px] leading-[130%] font-black lg:text-[32px]">
            {hotTitle}
          </h2>

          <div className="flex items-center gap-2 shrink-0">
            <button className="btn-highlight-resource flex items-center justify-center w-[38px] h-[38px] bg-[#E4E8F1] rounded-[8px] cursor-pointer lg:hidden">
              <img src={`${rmVars.themeUrl}images/link-ico-6C7687.png`}
                className="w-[20px]"
                alt="filter"
              />
            </button>

            <button
              className="btn-filter flex items-center justify-center w-[38px] h-[38px] bg-[#E4E8F1] rounded-[8px] cursor-pointer lg:hidden"
            >
              <img src={`${rmVars.themeUrl}images/filter-ico.png`}
                className="w-[20px]"
                alt="filter"
              />
            </button>
          </div>
        </div>

        {/* Notable Publications 설명 (hot 필터 선택 시) */}
        {currentHot === 'hcp' && (
          <div className="bg-[#F5F6F8] px-0 pt-[10px] pb-[10px]">
            <p className="mb-0 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              Highly Cited Papers(HCP) are papers that perform in the top 1% based on the number of
              citations received when compared to other papers published in the same field in the same year.
            </p>
            <p className="mt-2 mb-2 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              최근 10년 간 동일한 분야에서 동일한 연도에 출판된 다른 논문들과 비교했을 때, 인용 횟수 기준 상위 1%에 해당하는 논문
            </p>
          </div>
        )}

        {currentHot === 'hp' && (
          <div className="bg-[#F5F6F8] px-0 pt-[10px] pb-[10px]">
            <p className="mb-0 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              Hot Papers are papers published in the last two years that are receiving citations
              quickly after publication. These papers have been cited enough times in the most
              recent bimonthly period to place them in the top 0.1% when compared to papers in the
              same field and added to the database in the same period.
            </p>
            <p className="mt-2 mb-2 text-[12px] leading-[1.5] text-[#9CA3AF] antialiased">
              최근 2년 이내에 출판된 논문 중, 출판 직후 짧은 기간에 빠르게 인용이 증가하고 있는 논문
            </p>
          </div>
        )}

        <PublicationGraphs
          graphExpanded={graphExpanded}
          setGraphExpanded={setGraphExpanded}
          marginTopClass={currentHot === 'hcp' || currentHot === 'hp' ? 'mt-[2px] min-[800px]:mt-[17px]' : 'mt-[18px] min-[800px]:mt-[37px]'}
        />

        <div className="listUtil mt-10 lg:flex items-center justify-between">
          <div className="mb-3 text-[14px] text-[#6C7687] lg:mb-0">
            Found <strong>{(pageInfo.totalResults && Number(pageInfo.totalResults).toLocaleString()) || 0}</strong> results
          </div>

          <PublicationSortSelect
            sort={sort}
            onChange={(nextSort) => {
              setSort(nextSort);
              syncUrl({ page: 1, sort: nextSort });
              // 새로운 sort 값을 직접 사용하여 extra 파라미터 생성
              const extra = buildExtraParams();
              if (nextSort?.order_by) extra.order_by = nextSort.order_by;
              if (nextSort?.order) extra.order = nextSort.order;
              fetchList(1, { extra });
            }}
          />
        </div>

        <div className="relative mt-4 lg:mt-3">
          {loading && (
            <ChartSkeletonLoader show={loading} />
          )}
          {rows.length !== 0 && (<ul className="journal-list-wrap flex flex-col gap-3">
            {rows.map((r) => (<PublicationCard key={r.resource_id || r.id || r.issn || Math.random()} item={r} highlightQuery={query} />))}
          </ul>)}

          {rows.length === 0 && !loading && (<div className="no-journal-list hidden">
            <div className="text-[13px] font-semibold text-center bg-white py-[72px] rounded-xl">
              조건에 맞는 검색 결과가 없습니다.
            </div>
          </div>)}
        </div>

        <Paginator pageInfo={pageInfo} onPage={onPage} />

        <FacetPopup
          open={!!popup.open}
          title={popup.title}
          items={arr(facet?.[popup.key]?.result)}
          selected={pendingFilters?.[popup.key] || []}
          facetKey={popup.key}
          conjunction={popup.conjunction}
          conjunctionValue={popup.conjunctionValue}
          onConjunctionChange={popup.onConjunctionChange}
          onToggle={(value) => {
            const k = popup.key;
            if (!k) return;
            setPendingFilters(prev => {
              const prevArr = Array.isArray(prev[k]) ? prev[k] : [];
              const exists = prevArr.includes(value);
              const nextArr = exists ? prevArr.filter(v => v !== value) : [...prevArr, value];
              return { ...prev, [k]: nextArr };
            });
          }}
          onRefine={() => {
            const next = cleanFilters(pendingFilters || {});
            setFilters(next);
            setPendingFilters(next);
            // 그래프 닫기
            setGraphExpanded(false);
            const extra = {};
            Object.entries(next).forEach(([k, vals]) => {
              if (Array.isArray(vals) && vals.length) extra[`${k}[]`] = vals;
            });
            // conjunction 파라미터 추가
            Object.entries(conjunctions).forEach(([k, conj]) => {
              if (conj === 'and') {
                extra[`${k}_conjunction`] = 'and';
              }
            });
            if (sort?.order_by) extra.order_by = sort.order_by;
            if (sort?.order) extra.order = sort.order;
            if (String(query || '').trim()) extra.sub_ks = String(query).trim();
            syncUrl({ page: 1, filters: next, sort, conjunctions });
            try {
              if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
              }
            } catch (e) {
            }
            setPopup(p => ({ ...p, open: false }));
            fetchList(1, { extra });
          }}
          onCancel={() => setPopup(p => ({ ...p, open: false }))}
        />
      </div>
    </div>
  );
}
